import {
  ChangeDetectorRef,
  HostBinding,
  Injectable,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { merge, Subject } from 'rxjs';
import { FormAlignmentsEnum } from '../../form-master.enum';
import { IFormFieldErrorMessages } from '../../form-master.interface';
import { filter, takeUntil } from 'rxjs/operators';

// should only be used as a superclass; this class is not injectable
@Injectable()
export class FormFieldComponent implements OnInit, OnDestroy {
  @HostBinding('attr.data-alignment')
  get componentAlignment(): FormAlignmentsEnum {
    return this.alignment || FormAlignmentsEnum.left;
  }

  protected destroyed: Subject<boolean> = new Subject<boolean>();
  protected errorMessages: IFormFieldErrorMessages;

  alignment: FormAlignmentsEnum;
  fieldControl: AbstractControl;

  constructor(
    protected changeDetectorRef: ChangeDetectorRef,
    protected _translateService: TranslateService
  ) {}

  ngOnInit() {
    if (this.fieldControl) {
      merge(this.fieldControl.valueChanges, this.fieldControl.statusChanges)
        .pipe(
          takeUntil(this.destroyed),
          filter(() => this.fieldControl.pristine)
        )
        .subscribe(() => this.changeDetectorRef.markForCheck());

      (this.fieldControl as FormControl).registerOnChange(() =>
        this.changeDetectorRef.markForCheck()
      );

      // overwrite markAsDirty fn to trigger change detection (to trigger error message visibility)
      const markAsDirtyFn = this.fieldControl.markAsDirty.bind(
        this.fieldControl
      );
      this.fieldControl.markAsDirty = () => {
        const result = markAsDirtyFn();

        this.changeDetectorRef.markForCheck();

        return result;
      };
    }
  }

  get errorMessage(): string {
    return this.getTranslatedErrorMessage(this.fieldControl.errors);
  }

  protected getTranslatedErrorMessage(errors: ValidationErrors): string {
    errors = errors || {};

    const errorMessages = this.errorMessages || {};
    let key =
      (errorMessages.generic && errorMessages.generic.message) ||
      'ERROR.FIELD_INVALID';
    let params = {};

    if (errors.required) {
      key =
        (errorMessages.required && errorMessages.required.message) ||
        'ERROR.FIELD_REQUIRED';
      params =
        (errorMessages.required && errorMessages.required.translateParams) ||
        {};
    } else if (errors.maxlength) {
      key =
        (errorMessages.maxlength && errorMessages.maxlength.message) ||
        'ERROR.FIELD_MAX_LENGTH';
      params = (errorMessages.maxlength &&
        errorMessages.maxlength.translateParams) || {
        maxLength: errors.maxlength.requiredLength,
      };
    } else if (errors.digits) {
      key =
        (errorMessages.digits && errorMessages.digits.message) ||
        'ERROR.FIELD_DIGITS';
      params =
        (errorMessages.digits && errorMessages.digits.translateParams) || {};
    } else if (errors.number) {
      key =
        (errorMessages.number && errorMessages.number.message) ||
        'ERROR.FIELD_NUMBER';
      params =
        (errorMessages.number && errorMessages.number.translateParams) || {};
    } else if (errors.range) {
      key =
        (errorMessages.range && errorMessages.range.message) ||
        'ERROR.FIELD_RANGE';
      params =
        (errorMessages.range && errorMessages.range.translateParams) || {};
    } else if (errors.email) {
      key =
        (errorMessages.email && errorMessages.email.message) ||
        'ERROR.FIELD_EMAIL';
      params =
        (errorMessages.email && errorMessages.email.translateParams) || {};
    } else if (errors.pattern) {
      // TODO: only works if pattern is used for email address.
      key =
        (errorMessages.pattern && errorMessages.pattern.message) ||
        'ERROR.FIELD_EMAIL';
      params =
        (errorMessages.pattern && errorMessages.pattern.translateParams) || {};
    } else if (errors.min) {
      key =
        (errorMessages.min && errorMessages.min.message) || 'ERROR.FIELD_MIN';
      params = (errorMessages.min && errorMessages.min.translateParams) || {
        min: errors.min.min,
      };
    } else if (errors.max) {
      key =
        (errorMessages.max && errorMessages.max.message) || 'ERROR.FIELD_MAX';
      params = (errorMessages.max && errorMessages.max.translateParams) || {
        max: errors.max.max,
      };
    } else if (errors.duplicatedRate) {
      // custom
      key =
        (errorMessages.duplicatedRate &&
          errorMessages.duplicatedRate.message) ||
        'ERROR.FIELD_DUPLICATED_RATE';
      params =
        (errorMessages.duplicatedRate &&
          errorMessages.duplicatedRate.translateParams) ||
        {};
    }

    return this._translateService.instant(key, params);
  }

  ngOnDestroy() {
    this.destroyed.next(true);
    this.destroyed.unsubscribe();
  }
}
